/* eslint-disable react-hooks/exhaustive-deps */
import {
  BarcodeOutlined,
  LoadingOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import { Modal, Spin } from 'antd';
import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { getProductDetails, scanProduct } from '../../redux/actions/products';
import { formatCurrency, getApplicableTierPrice, getEffectivePrice, getProductImageUrl } from '../../utils';

const AUTO_ADD_TIMEOUT_SECONDS = 3;

const ScannedProductModal = forwardRef(({
  product: productProp,
  barcode,
  customer_netsuite_id,
  isOpen,
  onClose,
  onAddToCart,
  orderId,
  initialQuantity = 1,
}, ref) => {
  const [product, setProduct] = useState(() => {
    if (!productProp) return null;
    return {
      ...productProp,
      unit_price: productProp.unit_price,
    };
  });
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState(initialQuantity);
  const [timeLeft, setTimeLeft] = useState(AUTO_ADD_TIMEOUT_SECONDS);
  const isAddingRef = useRef(false);
  const fetchTiersPromise = useRef(null);

  const fetchPriceLevels = async (prod = product) => {
    if (prod?.tiers) return prod.tiers;
    if (fetchTiersPromise.current) return fetchTiersPromise.current;

    const pId = prod?.product_netsuite_id || prod?.netsuite_id || prod?.item_internal_id || prod?.id;
    if (!pId) return [];

    fetchTiersPromise.current = getProductDetails({
      id: pId,
      details: 1,
      loading: false,
    })
      .then((res) => {
        const rawTiers = res?.product || [];
        return rawTiers.map(t => ({
          price_level: t.price_level,
          tier_price: t.unit_price,
          minimum_quantity: t.minimum_quantity,
          case_quantity: t.case_quantity
        }));
      })
      .finally(() => {
        fetchTiersPromise.current = null;
      });

    return fetchTiersPromise.current;
  };

  const handleChangeQuantity = async (newQty, prod = product, targetUom = prod?.uom) => {
    const qtyNum = typeof newQty === 'number' ? newQty : (parseInt(newQty, 10) || 1);
    const tiers = await fetchPriceLevels(prod);

    const price = getApplicableTierPrice({
      ...prod,
      tiers,
      uom: targetUom || 'Case',
      product_case_quantity: prod?.case_quantity || prod?.product_case_quantity || 1,
      quantity: qtyNum,
      price_level: prod?.price_level,
    });

    const newUnitPrice = price?.tier_price || price?.unit_price || prod?.unit_price;

    setProduct((prev) => ({
      ...prev,
      tiers,
      unit_price: newUnitPrice,
      uom: targetUom || 'Case',
      product_case_quantity: prod?.case_quantity || prod?.product_case_quantity || 1,
    }));
  };

  useEffect(() => {
    if (productProp) {
      const initialUnitPrice = productProp.unit_price;
      const initialUom = barcode
        ? barcode === productProp.product_item_barcode
          ? 'Each'
          : barcode === productProp.product_carton_barcode || barcode === productProp.code
            ? 'Case'
            : productProp.uom || 'Case'
        : productProp.uom || 'Case';

      const qtyToSet = initialQuantity;

      const initialProd = {
        ...productProp,
        unit_price: initialUnitPrice,
        uom: initialUom,
        product_case_quantity: productProp?.case_quantity || productProp?.product_case_quantity || 1,
      };
      setProduct(initialProd);
      setQuantity(qtyToSet);
      handleChangeQuantity(qtyToSet, initialProd, initialUom);
    }
  }, [productProp, initialQuantity, barcode]);

  useEffect(() => {
    if (isOpen) {
      if (!productProp) {
        setQuantity(initialQuantity);
      }
      if (barcode && !productProp && !orderId) {
        setLoading(true);
        scanProduct({ barcode, customer_netsuite_id, order_id: orderId })
          .then((res) => {
            if (res?.ack) {
              const prod = Array.isArray(res?.product)
                ? res.product[0]
                : res?.product || (Array.isArray(res?.products) ? res.products[0] : res?.products);
              if (prod) {
                const initialUnitPrice = prod.unit_price;
                const scannedUom = barcode === prod.product_item_barcode
                  ? 'Each'
                  : barcode === prod.product_carton_barcode || barcode === prod.code
                    ? 'Case'
                    : prod.uom || 'Case';

                const initialProd = {
                  ...prod,
                  unit_price: initialUnitPrice,
                  uom: scannedUom,
                  product_case_quantity: prod?.case_quantity || prod?.product_case_quantity || 1,
                };
                setProduct(initialProd);
                setQuantity(initialQuantity);
                handleChangeQuantity(initialQuantity, initialProd, scannedUom);
              }
            }
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }
  }, [isOpen, barcode, productProp, orderId]);

  const handleAutoAdd = async () => {
    if (isAddingRef.current) return;
    isAddingRef.current = true;

    const finalQty = quantity === '' ? 1 : Math.min(999, Math.max(1, Number(quantity)));
    let productToAdd = product;

    try {
      const tiers = await fetchPriceLevels();
      if (tiers?.length) {
        const price = getApplicableTierPrice({
          ...product,
          tiers,
          uom: product?.uom || 'Case',
          product_case_quantity: product?.case_quantity || product?.product_case_quantity || 1,
          quantity: finalQty,
          price_level: product?.price_level,
        });

        const calculatedPrice = price?.tier_price || price?.unit_price || product?.unit_price;
        if (calculatedPrice) {
          productToAdd = { ...product, tiers, unit_price: calculatedPrice };
        }
      }
    } catch (e) {
      console.error(e);
    }

    if (onAddToCart) {
      await onAddToCart(productToAdd, finalQty);
    }
    if (onClose) {
      onClose();
    }
  };

  const handleScanAgain = (newBarcode) => {
    setTimeLeft(AUTO_ADD_TIMEOUT_SECONDS);
    const newUom = newBarcode === productProp.product_item_barcode
      ? 'Each'
      : newBarcode === productProp.product_carton_barcode || newBarcode === productProp.code
        ? 'Case'
        : product.uom || 'Case';

    if (newUom !== product.uom) {
      setQuantity(1);
      handleChangeQuantity(1, product, newUom);
    } else {
      setQuantity(prev => {
        const nextQty = (Number(prev) || 0) + 1;
        handleChangeQuantity(nextQty, product, newUom);
        return nextQty;
      });
    }
  };

  useImperativeHandle(ref, () => ({
    handleAutoAdd,
    handleScanAgain,
  }));

  useEffect(() => {
    let timer;
    if (isOpen && product && !loading) {
      setTimeLeft(AUTO_ADD_TIMEOUT_SECONDS);
      isAddingRef.current = false;
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            handleAutoAdd();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      setTimeLeft(AUTO_ADD_TIMEOUT_SECONDS);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isOpen, product, loading]);

  if (!product && !loading) return null;

  if (loading) {
    return (
      <Modal
        open={isOpen}
        onCancel={onClose}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
        className="scanned-product-modal"
        width={460}
        zIndex={2000}
        styles={{
          body: { padding: 0 },
        }}
      >
        <div className="scanned-modal-card" style={{ padding: '40px 20px', textAlign: 'center' }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 36, color: '#F58220' }} spin />} />
          <div style={{ marginTop: 16, fontSize: 16, color: '#4B5563', fontWeight: 500 }}>
            Looking up product...
          </div>
        </div>
      </Modal>
    );
  }

  const effectiveUnitPrice = getEffectivePrice(product);
  const qty = quantity === '' ? 1 : Number(quantity) || 1;
  const totalPrice = effectiveUnitPrice * qty;
  const isTaxable = product.taxable === "Taxable" || product.vat;
  const vatAmount = isTaxable ? totalPrice * 0.20 : 0;
  const productImage = getProductImageUrl(product);


  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      closable={false}
      maskClosable={false}
      centered
      className="scanned-product-modal"
      width={460}
      zIndex={2000}
      styles={{
        body: { padding: 0 },
      }}
    >
      <div className="scanned-modal-card">
        {/* Header with Title and Close Button */}
        <div className="scanned-modal-header">
          <div className="scanned-modal-header-tag">
            <BarcodeOutlined />
            <span>SCANNED PRODUCT</span>
          </div>
        </div>

        {/* Product Visual & Info Area */}
        <div className="scanned-modal-body">
          <div className="scanned-img-box">
            {(product?.product_code || product?.code) && (
              <div className="scanned-product-code-badge" title={product?.product_code || product?.code}>
                {product?.product_code || product?.code}
              </div>
            )}
            {productImage ? (
              <img
                src={productImage}
                alt={product.name}
                className="scanned-product-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div
              className="scanned-image-placeholder"
              style={{ display: productImage ? 'none' : 'flex' }}
            >
              <PictureOutlined />
            </div>
          </div>

          <div className="scanned-product-details">
            {/* Category badge */}
            {product.sub_category && (
              <span className="scanned-cat-badge">{product.sub_category}</span>
            )}

            <h3 className="scanned-product-title" title={product.name}>
              {product.name}
            </h3>

            <div className="scanned-price-box">
              <div className="scanned-unit-price">
                <span className="price-num">{formatCurrency(effectiveUnitPrice)}</span>
                {isTaxable && <span className="scanned-vat-tag">+VAT</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Quantity & Total Calculation Section */}
        <div className="scanned-modal-controls">
          <div className="scanned-qty-selector-wrap" style={{ display: 'flex', gap: '56px', alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span className="scanned-section-label">UOM</span>
              <span style={{ fontSize: '15px', fontWeight: 800, color: '#111827' }}>
                {product?.uom || 'Case'}
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span className="scanned-section-label">Quantity</span>
              <span style={{ fontSize: '15px', fontWeight: 800, color: '#111827' }}>
                {quantity}
              </span>
            </div>
          </div>

          <div className="scanned-total-box">
            <span className="scanned-total-label">Subtotal</span>
            <div className="scanned-total-amount">
              {formatCurrency(totalPrice + vatAmount)}
              {isTaxable && (
                <span className="scanned-total-vat-hint">(inc. VAT)</span>
              )}
            </div>
          </div>
        </div>

        {/* Timer Footer */}
        <div className="scanned-modal-header" style={{ borderTop: '1px solid #f3f4f6', borderBottom: 'none', justifyContent: 'center' }}>
          <div className="scanned-modal-header-tag" style={{ color: '#6B7280' }}>
            <span>ADDING TO CART IN {timeLeft}S</span>
          </div>
        </div>
      </div>
    </Modal>
  );
});

export default ScannedProductModal;
